import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Mail, Phone, ThumbsUp, ThumbsDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PhotoGallery } from "@/components/dealer/PhotoGallery";
import { StatusPill, estText, yearOf, isManual, apiValue } from "./Enquiries";
import { api, errMsg, fmtDateTime, telHref, gbp } from "@/lib/api";
import { cleanName } from "@/components/site/EstimateCard";
import { conditionText } from "@/components/site/ConditionSlider";

const REASONS = {
  specialist_vehicle: "Specialist or performance car", over_15000: "Valued above £15,000", poor_condition: "Customer rated the condition 1–2 out of 10",
  low_confidence: "Valuation data looked unreliable",
};
const DETAILS = {
  missing_value: "trade values were missing", contradictory_values: "the valuation figures contradicted each other",
  inconsistent_values: "Trade – average was unusually far above Trade – poor", mileage_mismatch: "the valuation used a different mileage",
  vehicle_not_identified: "the vehicle wasn't identified",
  market_low: "DealerPricing's retail figure was well below what similar cars actually sold for",
  market_high: "DealerPricing's retail figure was well above what similar cars actually sold for",
};

const MarketNote = ({ m }) => (m?.expected ? (
  <span className="text-sm font-normal text-slate-700" data-testid="detail-market">
    {m.n} similar cars sold ({m.miles_range[0].toLocaleString()}–{m.miles_range[1].toLocaleString()} miles): typical sold price at this mileage {gbp(m.low)}–{gbp(m.high)}. DealerPricing retail {gbp(m.dp_retail)}.
  </span>
) : <span className="text-sm font-normal text-slate-500" data-testid="detail-market">Not enough comparable sold cars to check.</span>);

const ProposedPrice = ({ e, onSaved }) => {
  const [v, setV] = useState(e.final_offer?.amount ?? "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!(Number(v) > 0)) return toast.error("Please enter a price.");
    setBusy(true);
    try {
      const { data } = await api.patch(`/dealer/enquiries/${e.id}`, { final_offer: Number(v) });
      onSaved(data);
      toast.success("Proposed price saved");
    } catch (x) {
      toast.error(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="proposed-price">
      <label htmlFor="pp" className="text-sm font-semibold text-slate-900">Your proposed purchase price</label>
      <div className="mt-1 flex gap-2">
        <input id="pp" type="number" inputMode="numeric" min="1" className="field-input w-40 py-2 text-lg" placeholder="e.g. 4250" value={v} onChange={(x) => setV(x.target.value)} data-testid="proposed-price-input" />
        <Button onClick={save} disabled={busy} className="h-11" data-testid="proposed-price-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
      </div>
      <p className="mt-1 text-xs text-slate-500">For your records only – the customer isn't told and the online valuation isn't changed.</p>
    </div>
  );
};

const Row = ({ k, v, testid }) => <><dt className="text-slate-500">{k}</dt><dd className="font-semibold text-slate-900" data-testid={testid}>{v}</dd></>;
const INTERESTED = ["interested", "awaiting_customer", "ready_for_review", "offer_sent", "inspection_arranged", "purchased"];
const statusOf = (s) => (INTERESTED.includes(s) ? "interested" : ["declined", "closed"].includes(s) ? "declined" : "new");

const Decision = ({ e, onSaved }) => {
  const [busy, setBusy] = useState(null);
  const cur = statusOf(e.status);
  const set = async (status) => {
    setBusy(status);
    try {
      const { data } = await api.patch(`/dealer/enquiries/${e.id}`, { status });
      onSaved(data);
      toast.success(status === "interested" ? "Marked as Interested" : status === "declined" ? "Marked as Not Interested" : "Moved back to New");
    } catch (x) {
      toast.error(errMsg(x));
    } finally {
      setBusy(null);
    }
  };
  const btn = (status, label, Icon, on) => (
    <Button onClick={() => set(status)} disabled={!!busy || cur === status} variant={on ? "default" : "outline"}
            className={`h-12 flex-1 text-base ${on ? (status === "interested" ? "btn-cta" : "!bg-slate-700 text-white") : ""} disabled:opacity-100`}
            aria-pressed={on} data-testid={`decision-${status}-button`}>
      {busy === status ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Icon className="mr-2 h-4 w-4" />}{label}
    </Button>
  );
  return (
    <div data-testid="decision-panel">
      <div className="flex flex-col gap-2 sm:flex-row">
        {btn("interested", "Interested", ThumbsUp, cur === "interested")}
        {btn("declined", "Not Interested", ThumbsDown, cur === "declined")}
      </div>
      <p className="mt-2 text-sm text-slate-600">Nothing is sent to the customer. Contact them yourself using the details below.</p>
      {cur !== "new" && <button onClick={() => set("new")} className="link mt-1 text-sm" data-testid="decision-reset-button">Move back to New</button>}
    </div>
  );
};

export default function EnquiryDetail() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/dealer/enquiries/${id}`).then((r) => setE(r.data)).catch((x) => setErr(errMsg(x))); }, [id]);
  if (err) return <p className="text-destructive" data-testid="enquiry-error">{err}</p>;
  if (!e) return <p className="text-slate-500">Loading…</p>;
  const c = e.contact;
  const name = cleanName(e.vehicle_description);
  const vi = e.vehicle_info || {};
  const manual = isManual(e);
  const est = manual ? null : estText(e);
  const val = e.valuation || {};
  const note = REASONS[val.reason] ? `${REASONS[val.reason]}${DETAILS[val.specialist] ? ` – ${DETAILS[val.specialist]}` : ""}. No price was shown to the customer.` : null;
  const tp = val.figures?.["figures.trade_poor"] ?? val.dealer_estimate?.trade_poor;
  const ta = val.figures?.["figures.trade_average"] ?? val.dealer_estimate?.trade_average;
  const used = e.valuation?.provider_mileage;
  const comments = [vi.comments, vi.faults_details, vi.damage_details].filter(Boolean).join("\n");
  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="enquiry-detail">
      <Link to="/dealer" className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="back-link"><ArrowLeft className="h-4 w-4" />Back to enquiries</Link>
      {e.is_test && <p className="rounded-md bg-amber-100 px-3 py-2 text-sm font-semibold text-amber-900" data-testid="test-enquiry-label">TEST ENQUIRY – not a real customer</p>}
      <section className="panel p-5" data-testid="vehicle-card">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h1 className="text-2xl font-extrabold text-slate-900 sm:text-3xl" data-testid="enquiry-title">{yearOf(e) ? `${yearOf(e)} ` : ""}{name || "Vehicle details unavailable"}</h1>
          <StatusPill s={e.status} />
        </div>
        {!e.vehicle_description && <p className="mt-1 text-sm text-slate-600" data-testid="vehicle-unavailable-note">The valuation service didn't identify this vehicle – check the registration with the customer.</p>}
        <dl className="mt-4 grid grid-cols-[8.5rem_1fr] gap-x-4 gap-y-2.5 text-base">
          <Row k="Registration" v={<span className="plate-sm">{e.registration}</span>} />
          <Row k="Mileage" v={`${Number(e.mileage).toLocaleString("en-GB")} miles`} testid="detail-mileage" />
          <Row k="Condition" v={val.condition ? <>{val.condition}/10 · {conditionText(val.condition)[0]}{val.condition_changes > 0 && <span className="block text-sm font-normal text-slate-500" data-testid="condition-changes">Customer changed the rating {val.condition_changes} time{val.condition_changes > 1 ? "s" : ""} before sending</span>}</> : "Not given"} testid="detail-condition" />
          <Row k={manual ? "Valuation" : "Quoted price"} v={est ? <>{est} <span className="text-sm font-normal text-slate-500">(shown to customer{val.mode === "sandbox" ? " – sandbox test" : ""})</span></> : <>Manual Review Required{note && <span className="block text-sm font-normal text-slate-600" data-testid="specialist-note">{note}</span>}{apiValue(e) && <span className="block text-sm font-normal text-slate-700" data-testid="detail-api-value">Internal estimate: {apiValue(e)}</span>}</>} testid="detail-estimate" />
          {(tp || ta) && <Row k="DealerPricing" v={<span className="text-sm font-normal text-slate-700">Trade – poor {tp ? gbp(tp) : "–"} · Trade – average {ta ? gbp(ta) : "–"}</span>} testid="detail-dp-figures" />}
          {val.market && <Row k="Sold-car check" v={<MarketNote m={val.market} />} testid="detail-market-row" />}
          <Row k="Location" v={c.postcode || "Not given"} testid="detail-postcode" />
          <Row k="Received" v={fmtDateTime(e.created_at)} />
        </dl>
        {!e.is_test && used != null && Number(used) !== Number(e.mileage) && (
          <p className="mt-3 rounded border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert" data-testid="mileage-mismatch-warning">
            Mileage check: the customer entered {Number(e.mileage).toLocaleString("en-GB")} miles, but the valuation used {Number(used).toLocaleString("en-GB")} miles.
          </p>
        )}
        {comments && (
          <div className="mt-4 border-t border-border pt-3" data-testid="customer-comments">
            <p className="text-sm text-slate-500">Customer's comments</p>
            <p className="mt-1 whitespace-pre-line text-slate-900">{comments}</p>
          </div>
        )}
      </section>
      <section className="panel space-y-5 p-5"><Decision e={e} onSaved={setE} /><ProposedPrice e={e} onSaved={setE} /></section>
      <section className="panel p-5" data-testid="customer-card">
        <h2 className="text-lg font-bold text-slate-900">Customer Details</h2>
        <dl className="mt-3 grid grid-cols-[8.5rem_1fr] gap-x-4 gap-y-2.5 text-base">
          <Row k="Name" v={c.name} testid="customer-name" />
          <Row k="Telephone" v={<a className="link" href={telHref(c.phone)} data-testid="customer-phone-link">{c.phone}</a>} />
          <Row k="Email" v={c.email ? <a className="link break-all" href={`mailto:${c.email}?subject=${encodeURIComponent(`Your ${name || "car"} (${e.registration})`)}`} data-testid="customer-email-link">{c.email}</a> : "Not given"} />
          <Row k="Postcode" v={c.postcode || "Not given"} />
        </dl>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Button asChild className="h-11"><a href={telHref(c.phone)} data-testid="customer-call-link"><Phone className="mr-2 h-4 w-4" />Call customer</a></Button>
          {c.email && <Button asChild variant="outline" className="h-11"><a href={`mailto:${c.email}`} data-testid="customer-email-button"><Mail className="mr-2 h-4 w-4" />Email customer</a></Button>}
        </div>
      </section>
      {(e.photos || []).length > 0 && <section className="panel p-5" data-testid="photos-card"><h2 className="mb-3 text-lg font-bold">Photos</h2><PhotoGallery photos={e.photos} /></section>}
      <p className="text-xs text-slate-500" data-testid="enquiry-reference">Reference {e.reference}</p>
    </div>
  );
}
